import { useCallback, useEffect, useRef, useState } from 'react'
import { useSetAtom } from 'jotai'
import { ChallengeService } from '@duck/api-definitions'
import { useDebouncedWorkSaver } from '@/hooks/useDebouncedWorkSaver'
import { workspaceTabsHasTwoViewsAtom } from '@/store/workspaceAtoms'

export type ViewTabState = ChallengeService.WorkspaceTab

export type ViewState = {
    tabs: ViewTabState[]
    activeTabIndex: number
}

type TabViews = NonNullable<ChallengeService.WorkState['tabViews']>

// Convert component view state to contract tabViews
const viewsToContractTabViews = (views: ViewState[], widths?: number[]): TabViews => {
    return views.map((view, index): ChallengeService.WorkspaceTabView => ({
        activeTab: view.activeTabIndex,
        tabs: view.tabs.map(
            (tab): ChallengeService.WorkspaceTab => ({
                type: tab.type,
                data: tab.data,
            })
        ),
        width: widths?.[index],
    }))
}

export interface UseWorkspaceViews {
    /** View state for rendering. Note: tab.data here is intentionally stale for content edits — read getLiveViews() for the latest. */
    views: ViewState[]
    panelWidths: number[]
    /** Live view state including content edits (e.g. SQL typing) that are persisted without a rerender. */
    viewsRef: React.MutableRefObject<ViewState[]>
    /** Latest view state — always prefer this over `views` when computing a structural change. */
    getLiveViews: () => ViewState[]
    /** Structural change (split/merge/add/remove/reorder/switch): updates state + ref, syncs hasTwoViews, persists. Triggers a rerender. */
    commitStructural: (newViews: ViewState[], newWidths?: number[]) => void
    /** Content-only change (e.g. SQL typing): persists via ref without a rerender. */
    updateLiveView: (viewIndex: number, view: ViewState) => void
    /** Panel resize: updates width state + ref and persists, without touching views. */
    commitWidths: (widths: number[]) => void
    /** Replace all state from freshly loaded server data (no save). */
    resetFromServer: (newViews: ViewState[], newWidths: number[]) => void
}

/**
 * Owns the workspace tab-view layout state.
 *
 * Content edits (SQL typing) are deliberately kept OUT of `views` React state to avoid
 * re-rendering — and remounting — the editor on every keystroke. They live only in
 * `viewsRef.current`. The hazard: any structural change (split/merge/add/remove) that
 * rebuilds the layout from stale `views` state drops those edits, and the ensuing remount
 * resets the editor to its default. Every structural handler must therefore read
 * `getLiveViews()` and write through `commitStructural()`, which this hook enforces.
 */
export const useWorkspaceViews = (
    challengeId: string,
    initialViews: ViewState[],
    initialWidths: number[]
): UseWorkspaceViews => {
    const setHasTwoViews = useSetAtom(workspaceTabsHasTwoViewsAtom)
    const { schedule: scheduleWorkSave } = useDebouncedWorkSaver(challengeId)

    const [views, setViews] = useState<ViewState[]>(initialViews)
    const [panelWidths, setPanelWidths] = useState<number[]>(initialWidths)
    const viewsRef = useRef<ViewState[]>(initialViews)
    // Mirrors panelWidths so commit helpers can default to the latest widths without a stale closure.
    const widthsRef = useRef<number[]>(initialWidths)

    const save = useCallback(
        (viewsToSave: ViewState[], widths?: number[]) => {
            scheduleWorkSave({ tabViews: viewsToContractTabViews(viewsToSave, widths) })
        },
        [scheduleWorkSave]
    )

    const getLiveViews = useCallback(() => viewsRef.current, [])

    const commitStructural = useCallback(
        (newViews: ViewState[], newWidths?: number[]) => {
            const widths = newWidths ?? widthsRef.current
            viewsRef.current = newViews
            widthsRef.current = widths
            setViews(newViews)
            if (newWidths) setPanelWidths(newWidths)
            setHasTwoViews(newViews.length === 2)
            save(newViews, widths)
        },
        [save, setHasTwoViews]
    )

    const updateLiveView = useCallback(
        (viewIndex: number, view: ViewState) => {
            const current = viewsRef.current
            if (viewIndex >= current.length) return
            const next = [...current]
            next[viewIndex] = view
            viewsRef.current = next
            save(next, widthsRef.current)
        },
        [save]
    )

    const commitWidths = useCallback(
        (widths: number[]) => {
            widthsRef.current = widths
            setPanelWidths(widths)
            save(viewsRef.current, widths)
        },
        [save]
    )

    const resetFromServer = useCallback(
        (newViews: ViewState[], newWidths: number[]) => {
            viewsRef.current = newViews
            widthsRef.current = newWidths
            setViews(newViews)
            setPanelWidths(newWidths)
            setHasTwoViews(newViews.length === 2)
        },
        [setHasTwoViews]
    )

    // Sync the two-views atom on mount.
    useEffect(() => {
        setHasTwoViews(viewsRef.current.length === 2)
    }, []) // Only run on mount

    return {
        views,
        panelWidths,
        viewsRef,
        getLiveViews,
        commitStructural,
        updateLiveView,
        commitWidths,
        resetFromServer,
    }
}
